import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import U2000InputMemo from "../../../../../u2000Core/components/formControls/input/U2000InputMemo";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputTag from "../../../../../u2000Core/components/formControls/input/U2000InputTag";
import { U2000InputTagEnum } from "../../../../../u2000Core/components/formControls/input/U2000InputTag";
import U2000InputMask from "../../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000InputCalendarCustom from "../../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000Panel from "../../../../../u2000Core/components/layout/U2000Panel";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { isValidId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { NS, U3121ROLES } from "../../../../U3121";
import type { U3121Project, U3121DueDateType } from "../../../../U3121Service";
import { emptyProjectToSurvey } from "../../../../U3121Service";
import { useU3121FindCountriesHandlers, useU3121FindStatesHandlers, useU3121FindLandSurveyorHandlers, type U3121FindSearchCriterias } from "../../../../U3121UseFindHandlers";
import { useU3121Context } from "../../../../U3121Context";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { U3141ROLES } from "../../../../../u3141LandRegister/U3141";
import U3121CreateLandRegisterButton from "../../../../components/U3121CreateLandRegisterButton";
import U3121GoogleCalendarSyncStatus from "../../../../components/common/U3121GoogleCalendarSyncStatus";
import U3121CopyCustomerAddressLink from "../../../../components/common/U3121CopyCustomerAddressLink";

const PREFIX = "projectToSurvey.";

interface U3121ProjectProjectToSurveyBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121Project>;
  t: TFunction;
}

function setNestedValueCloned<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const cloned = JSON.parse(JSON.stringify(obj)) as T;
  let current: Record<string, unknown> = cloned as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (!current[k] || typeof current[k] !== "object") {
      current[k] = {};
    }
    current = current[k] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
  return cloned;
}

function U3121ProjectProjectToSurveyBody({ formStateHandlers, t }: U3121ProjectProjectToSurveyBodyProps) {
  const { i18n } = useTranslation();
  const { initLU } = useU3121Context();
  const { hasRole } = useU2000AuthContext();
  const previousCountryIdRef = useRef<string | null>(null);
  const findCountriesHandlers = useU3121FindCountriesHandlers();
  const findStatesHandlers = useU3121FindStatesHandlers();
  const findLandSurveyorHandlers = useU3121FindLandSurveyorHandlers();

  const data = formStateHandlers.getData();
  const pts = data?.projectToSurvey;

  const countryResultFields = useMemo(() => {
    if (i18n.language === "fr") return ["countryNameFr"];
    if (i18n.language === "en") return ["countryNameEn"];
    return ["countryName"];
  }, [i18n.language]);

  const stateResultFields = useMemo(() => {
    if (i18n.language === "fr") return ["stateNameFr"];
    if (i18n.language === "en") return ["stateNameEn"];
    return ["stateName"];
  }, [i18n.language]);

  const onSearchCountry = findCountriesHandlers.search;

  const displayCountry = (item: Record<string, unknown>) => {
    if (!item) return "";
    if (i18n.language === "fr" && item.countryNameFr) return String(item.countryNameFr);
    if (i18n.language === "en" && item.countryNameEn) return String(item.countryNameEn);
    return String(item.countryName ?? item.name ?? "");
  };

  const extractCountryData = (result: Record<string, unknown> | null) => {
    if (result) {
      const countryName =
        (i18n.language === "fr" && result.countryNameFr) || (i18n.language === "en" && result.countryNameEn)
          ? String(i18n.language === "fr" ? result.countryNameFr : result.countryNameEn)
          : String(result.countryName ?? result.name ?? "");
      return [
        { fieldName: `${PREFIX}country.id`, value: result.id },
        { fieldName: `${PREFIX}country.countryNameFr`, value: result.countryNameFr ?? "" },
        { fieldName: `${PREFIX}country.countryNameEn`, value: result.countryNameEn ?? "" },
        { fieldName: `${PREFIX}country.countryName`, value: countryName },
      ];
    }
    return [
      { fieldName: `${PREFIX}country.id`, value: null },
      { fieldName: `${PREFIX}country.countryNameFr`, value: null },
      { fieldName: `${PREFIX}country.countryNameEn`, value: null },
      { fieldName: `${PREFIX}country.countryName`, value: null },
    ];
  };

  const onSearchState = async (criterias: U3121FindSearchCriterias) => {
    const countryId = data?.projectToSurvey?.country?.id;
    const crit = countryId && isValidId(countryId) ? [...criterias, { fieldName: "countryId", value: String(countryId) }] : criterias;
    return findStatesHandlers.search(crit);
  };

  const displayState = (item: Record<string, unknown>) => {
    if (!item) return "";
    if (i18n.language === "fr" && item.stateNameFr) return String(item.stateNameFr);
    if (i18n.language === "en" && item.stateNameEn) return String(item.stateNameEn);
    return String(item.stateName ?? item.name ?? "");
  };

  const extractStateData = (result: Record<string, unknown> | null) => {
    if (result) {
      const stateName =
        (i18n.language === "fr" && result.stateNameFr) || (i18n.language === "en" && result.stateNameEn)
          ? String(i18n.language === "fr" ? result.stateNameFr : result.stateNameEn)
          : String(result.stateName ?? result.name ?? "");
      return [
        { fieldName: `${PREFIX}state.id`, value: result.id },
        { fieldName: `${PREFIX}state.stateNameFr`, value: result.stateNameFr ?? "" },
        { fieldName: `${PREFIX}state.stateNameEn`, value: result.stateNameEn ?? "" },
        { fieldName: `${PREFIX}state.stateName`, value: stateName },
      ];
    }
    return [
      { fieldName: `${PREFIX}state.id`, value: null },
      { fieldName: `${PREFIX}state.stateNameFr`, value: null },
      { fieldName: `${PREFIX}state.stateNameEn`, value: null },
      { fieldName: `${PREFIX}state.stateName`, value: null },
    ];
  };

  useEffect(() => {
    if (!data) return;
    const countryId = pts?.country?.id ?? null;
    const currentCountryId = countryId && isValidId(countryId) ? countryId : null;
    const previousCountryId = previousCountryIdRef.current;
    const countryChanged = previousCountryId !== null && previousCountryId !== currentCountryId;
    const countryCleared = previousCountryId !== null && currentCountryId === null;
    const hasState = pts?.state?.id && isValidId(pts.state.id);

    if ((countryChanged || countryCleared) && hasState) {
      let next = setNestedValueCloned(data, `${PREFIX}state.id`, null);
      next = setNestedValueCloned(next, `${PREFIX}state.stateName`, null);
      next = setNestedValueCloned(next, `${PREFIX}state.stateNameFr`, null);
      next = setNestedValueCloned(next, `${PREFIX}state.stateNameEn`, null);
      formStateHandlers.setData(next);
    }
    if (previousCountryId === null || previousCountryId !== currentCountryId) {
      previousCountryIdRef.current = currentCountryId;
    }
  }, [data, pts?.country?.id, pts?.state?.id, formStateHandlers]);

  const onSearchSurveyor = findLandSurveyorHandlers.search;

  const displaySurveyor = (item: Record<string, unknown>) => {
    const firstName = item.firstName as string;
    const lastName = item.lastName as string;
    const codeEmployee = item.codeEmployee as string;
    return `${firstName ?? ""} ${lastName ?? ""}`.trim() ? `${firstName} ${lastName} (${codeEmployee ?? ""})` : (codeEmployee ?? "");
  };

  const extractSurveyorData = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: `${PREFIX}surveyor.id`, value: result.id },
        { fieldName: `${PREFIX}surveyor.firstName`, value: result.firstName },
        { fieldName: `${PREFIX}surveyor.lastName`, value: result.lastName },
        { fieldName: `${PREFIX}surveyor.codeEmployee`, value: result.codeEmployee },
      ];
    }
    return [{ fieldName: `${PREFIX}surveyor`, value: null }];
  };

  const dueDateTypeOptions: { value: U3121DueDateType; label: string }[] = useMemo(
    () => [
      { value: "Approximate", label: t("dueDateTypeApproximate", { ns: NS }) },
      { value: "Concrete", label: t("dueDateTypeConcrete", { ns: NS }) },
      { value: "RushPriorityFile", label: t("dueDateTypeRushPriorityFile", { ns: NS }) },
    ],
    [t],
  );

  const hasCountry = pts?.country?.id && isValidId(pts.country.id);
  const includeProjectToSurvey = Boolean(data?.includeProjectToSurvey);
  const isReadMode = formStateHandlers.getFormState?.() === U2000FormStateEnumState.READ;
  const showCreateLandRegisterButton = isReadMode && includeProjectToSurvey && hasRole(U3141ROLES.ADD);
  const customerId = data?.customer?.id;
  const canCopyCustomerAddress = !isReadMode && Boolean(customerId && isValidId(customerId));

  const handleIncludeProjectToSurveyChange = (checked: boolean) => {
    const next = formStateHandlers.getData();
    if (!next) return;
    let updated: U3121Project = { ...next, includeProjectToSurvey: checked };
    if (checked) {
      if (!updated.projectToSurvey) {
        updated = {
          ...updated,
          projectToSurvey: {
            ...emptyProjectToSurvey,
            country: initLU.defaultCountry?.[0] ?? null,
            state: initLU.defaultState?.[0] ?? null,
          },
        };
      }
      const pts = updated.projectToSurvey;
      if (pts) {
        const countryEmpty = !pts.country?.id || !isValidId(pts.country.id);
        const stateEmpty = !pts.state?.id || !isValidId(pts.state.id);
        if (countryEmpty || stateEmpty) {
          const defaultCountry = countryEmpty && initLU.defaultCountry?.[0] ? initLU.defaultCountry[0] : pts.country;
          const defaultState = stateEmpty && initLU.defaultState?.[0] ? initLU.defaultState[0] : pts.state;
          updated = {
            ...updated,
            projectToSurvey: {
              ...pts,
              country: defaultCountry ?? pts.country,
              state: defaultState ?? pts.state,
            },
          };
        }
      }
    }
    formStateHandlers.setData(updated);
    if (!checked) {
      const errors = formStateHandlers.getErrors() ?? {};
      const filtered = Object.fromEntries(Object.entries(errors).filter(([key]) => !key.startsWith("projectToSurvey.")));
      if (Object.keys(filtered).length !== Object.keys(errors).length) {
        formStateHandlers.setErrors(filtered);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="col-span-full mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={includeProjectToSurvey}
            onChange={(e) => handleIncludeProjectToSurveyChange(e.target.checked)}
            disabled={formStateHandlers.getFormState?.() === U2000FormStateEnumState.READ}
            className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={t("lblIncludeProjectToSurveyProject", { ns: NS })}
            aria-checked={includeProjectToSurvey}
          />
          <span>{t("lblIncludeProjectToSurveyProject", { ns: NS })}</span>
        </label>
      </div>
      {!includeProjectToSurvey && <p className="col-span-full text-sm text-gray-500">{t("msgIncludeProjectToSurveyHint", { ns: NS })}</p>}
      {includeProjectToSurvey && (
        <>
          <U2000Panel
            title={
              <>
                <span>{t("lblProjectToSurveyAddress", { ns: NS })}</span>
                <U3121CopyCustomerAddressLink
                  enabled={canCopyCustomerAddress}
                  resolveParams={() => ({ customerId: formStateHandlers.getData()?.customer?.id })}
                  onCopied={(mapped) => {
                    const current = formStateHandlers.getData();
                    if (!current) return;
                    formStateHandlers.setData({
                      ...current,
                      projectToSurvey: {
                        ...current.projectToSurvey,
                        ...mapped,
                      },
                    });
                  }}
                />
              </>
            }
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-10">
              <div className="min-w-0 md:col-span-6">
                <U2000InputTag
                  formStateHandlers={formStateHandlers}
                  fieldName={`${PREFIX}lot`}
                  label={t("lblLot", { ns: NS })}
                  inputType={U2000InputTagEnum.ALPHANUM}
                  required
                  placeholder=""
                  inlineEnd={
                    showCreateLandRegisterButton && data?.id ? (
                      <U3121CreateLandRegisterButton projectId={data.id} enabled={showCreateLandRegisterButton} />
                    ) : undefined
                  }
                />
              </div>
              <div className="min-w-0 md:col-span-4">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}owner`} label={t("lblOwner", { ns: NS })} required />
              </div>
              <div className="md:col-span-10">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}address`} label={t("lblAddress", { ns: NS })} required />
              </div>
              <div className="min-w-0 md:col-span-5">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}city`} label={t("lblCity", { ns: NS })} required />
              </div>
              <div className="min-w-0 md:col-span-5">
                <U2000InputMask formStateHandlers={formStateHandlers} fieldName={`${PREFIX}postalCode`} label={t("lblPostalCode", { ns: NS })} mask="postalCodeCA" />
              </div>
              <div className="min-w-0 md:col-span-5">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName={`${PREFIX}country`}
                  mainFieldName="countryName"
                  label={t("lblCountry", { ns: NS })}
                  onSearch={onSearchCountry}
                  displayFn={displayCountry}
                  overrideExtractDataFn={extractCountryData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={countryResultFields}
                  required
                />
              </div>
              <div className="min-w-0 md:col-span-5">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName={`${PREFIX}state`}
                  mainFieldName="stateName"
                  label={t("lblState", { ns: NS })}
                  onSearch={onSearchState}
                  displayFn={displayState}
                  overrideExtractDataFn={extractStateData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={stateResultFields}
                  readonly={!hasCountry}
                  required
                />
              </div>
            </div>
          </U2000Panel>

          <U2000Panel title={t("lblProjectToSurveyDueAndSurveyor", { ns: NS })}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName={`${PREFIX}dueDate`} label={t("lblDueDate", { ns: NS })} required />
              <U2000ComboBox
                formStateHandlers={formStateHandlers}
                fieldName={`${PREFIX}dueDateType`}
                label={t("lblDueDateType", { ns: NS })}
                data={dueDateTypeOptions}
                keyField="value"
                descField="label"
                getValue={(item) => item.value}
                findItemByValue={(value, arr) => arr.find((o) => o.value === value) ?? null}
                formatLabel={(item) => item.label}
                required
              />
              <div className="md:col-span-2">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}dueDateNote`} label={t("lblDueDateNote", { ns: NS })} />
              </div>
              <div className="md:col-span-2">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}deliveryTerms`} label={t("lblDeliveryTerms", { ns: NS })} />
              </div>
              <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${PREFIX}reference`} label={t("lblReference", { ns: NS })} />
              <div className="min-w-0">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName={`${PREFIX}surveyor`}
                  mainFieldName="codeEmployee"
                  entityLink="U3101"
                  label={t("lblSurveyor", { ns: NS })}
                  onSearch={onSearchSurveyor}
                  displayFn={displaySurveyor}
                  overrideExtractDataFn={extractSurveyorData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={["firstName", "lastName", "codeEmployee"]}
                />
              </div>
              <div className="md:col-span-2">
                <U3121GoogleCalendarSyncStatus
                  sync={pts?.googleCalendarSync}
                  t={t}
                  projectId={data?.id}
                  canAdopt={hasRole(U3121ROLES.EDIT)}
                  formStateHandlers={formStateHandlers}
                />
              </div>
            </div>
          </U2000Panel>

          <U2000Panel title={t("lblNote", { ns: NS })} className="col-span-full">
            <U2000InputMemo formStateHandlers={formStateHandlers} fieldName={`${PREFIX}note`} label={t("lblNote", { ns: NS })} hideLabel rows={4} />
          </U2000Panel>
        </>
      )}
    </div>
  );
}

export default U3121ProjectProjectToSurveyBody;
